import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { useCart } from '../../store/cartContext';
import { QuantityStepper } from './QuantityStepper';

export const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const productLinkState = { productPreview: product };

  useEffect(() => {
    setQty(1);
  }, [product.id]);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!product.isAvailable) return;
    addItem(product, qty);
    setQty(1);
  };

  return (
    <article className="product-card flex flex-col group">
      {/* Image */}
      <Link to={`/proizvodi/${product.id}`} state={productLinkState} className="block overflow-hidden relative">
        <div className="aspect-square overflow-hidden">
          <img
            src={product.primaryImageUrl}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {product.featured && (
            <span className="badge-gold text-xs">Izdvojeno</span>
          )}
          {!product.isAvailable && (
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-muted text-muted-foreground border border-border">
              Nema na lageru
            </span>
          )}
        </div>
      </Link>

      {/* Content */}
      <div className="flex flex-col flex-1 p-4">
        {/* Category */}
        <span className="text-xs font-ui text-muted-foreground mb-1.5">{product.categoryName}</span>

        {/* Name */}
        <Link to={`/proizvodi/${product.id}`} state={productLinkState}>
          <h3 className="font-serif text-base font-semibold text-foreground leading-snug mb-2 hover:text-primary transition-colors">
            {product.name}
          </h3>
        </Link>

        {/* Short desc */}
        <p className="text-sm text-muted-foreground font-body-serif leading-relaxed flex-1 mb-4 line-clamp-2">
          {product.shortDescription}
        </p>

        {/* Price + actions */}
        <div className="mt-auto">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="font-ui font-semibold text-lg text-foreground">
                {product.price.toLocaleString('sr-RS')} RSD
              </span>
              <span className="text-muted-foreground text-xs font-ui ml-1">/ {product.unit}</span>
            </div>
          </div>

          {product.isAvailable && (
            <div className="mb-3">
              <QuantityStepper
                label={`Količina (${product.unit})`}
                value={qty}
                onChange={setQty}
                min={1}
                max={99}
                size="sm"
              />
            </div>
          )}

          <div className="flex flex-col gap-2 sm:flex-row">
            <Link to={`/proizvodi/${product.id}`} state={productLinkState} className="w-full sm:flex-1">
              <Button
                variant="outline"
                size="sm"
                className="w-full border-border hover:border-primary hover:text-primary transition-colors font-ui"
              >
                <Eye size={14} className="mr-1.5" />
                Detalji
              </Button>
            </Link>
            <Button
              size="sm"
              disabled={!product.isAvailable}
              onClick={handleAddToCart}
              className="w-full sm:flex-1 bg-primary text-primary-foreground hover:bg-primary-light font-ui shadow-btn"
            >
              <ShoppingCart size={14} className="mr-1.5" />
              {product.isAvailable ? 'Dodaj' : 'Nedostupno'}
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
};
